import React, { useContext, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { merge } from 'lodash'
import { createTheme, ThemeProvider } from '@material-ui/core/styles'
import defaultTheme from '../../theme'
import { useAppConfig, useFirmColors } from '../../redux/slices/appConfig'
import { applyBrand, saveBrand } from '../../applyBrand'
import config from '../../config'
import { useThemeType } from '../../hooks/useThemeType'

function hexToHs (hex) {
  hex = hex.replace(/^#/, '')
  if (hex.length === 3) { hex = hex.split('').map(c => c + c).join('') }
  const num = parseInt(hex, 16)
  const r = (num >> 16) / 255
  const g = ((num >> 8) & 0xff) / 255
  const b = (num & 0xff) / 255

  const max = Math.max(r, g, b); const min = Math.min(r, g, b)
  let h; let s; const l = (max + min) / 2

  if (max === min) {
    h = s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    // eslint-disable-next-line default-case
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      case b: h = (r - g) / d + 4; break
    }
    h /= 6
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  }
}
const BrandContext = React.createContext({
  subdomain: config.subdomain
})

export const useSubdomain = () => {
  return useContext(BrandContext)?.subdomain ?? null
}

function BrandProvider ({ children }) {
  const themeType = useThemeType()
  const firmColors = useFirmColors()
  const appConfig = useAppConfig()
  useEffect(() => {
    try {
      if (appConfig?.pageConfig?.brand) {
        const brand = appConfig.pageConfig.brand
        applyBrand(brand)
        saveBrand(brand)
      }
    } catch (err) {
      console.error('unable to set branding', err)
    }
  }, [appConfig])

  const theme = useMemo(() => {
    const { cloudBurst, bluePalette, background } = defaultTheme.palette
    const accessors = {
      primary: themeType === 'dark' ? 'primaryDark' : 'primary',
      secondary: themeType === 'dark' ? 'secondaryDark' : 'secondary',
      accent: themeType === 'dark' ? 'accentDark' : 'accent'
    }
    const firmOverrides = {
      palette: {
        primary: {
          main: firmColors?.[accessors.primary] || cloudBurst,
          ...(firmColors?.contrastText ? { contrastText: firmColors?.contrastText } : {})
        },
        secondary: {
          main: firmColors?.[accessors.secondary] || bluePalette.main
        },
        accent: {
          main: firmColors?.[accessors.accent] || background.default
        }
      }
    }
    const brandTheme = appConfig?.pageConfig?.brand?.data?.theme

    const primary = hexToHs(firmOverrides.palette.primary.main)
    document.documentElement.style.setProperty('--primary-h', primary.h.toString())
    document.documentElement.style.setProperty('--primary-s', `${primary.s}%`)
    document.documentElement.style.setProperty('--primary-l', `${primary.l}%`)

    const secondary = hexToHs(firmOverrides.palette.secondary.main)
    document.documentElement.style.setProperty('--secondary-h', secondary.h.toString())
    document.documentElement.style.setProperty('--secondary-s', `${secondary.s}%`)
    document.documentElement.style.setProperty('--secondary-l', `${secondary.l}%`)

    const accent = hexToHs(firmOverrides.palette.accent.main)
    document.documentElement.style.setProperty('--accent-h', accent.h.toString())
    document.documentElement.style.setProperty('--accent-s', `${accent.s}%`)
    document.documentElement.style.setProperty('--accent-l', `${accent.l}%`)

    return createTheme(merge(defaultTheme, firmOverrides, brandTheme))
  }, [firmColors, appConfig, themeType])

  const contextValue = useMemo(() => {
    return {
      subdomain: config.subdomain
    }
  }, [])

  return (
    <BrandContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </BrandContext.Provider>
  )
}

BrandProvider.propTypes = {
  children: PropTypes.node
}

export default BrandProvider
